import { useEffect, useState } from 'react';
import { Button, Col, Form, Image, Input, Modal, Space, message } from 'antd';
import EmailTemplatePreviewButton, { getDesignFromFormValues } from './EmailTemplatePreviewButton';
import { TextService } from '../../Services/TextService';
import { BookyInput } from '../../Components/Forms/BookyInput';
import { BookyButton } from '../../Components/Buttons/BookyButton';
import { OneFrontHtmlEditor } from '../../Components/Forms/OneFrontHtmlEditor';
import { BookyCheckbox } from '../../Components/Forms/BookyCheckbox';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import { EmailTemplateType } from '../../Types/EmailTemplate';
import ImageSelect from '../../Components/Select/ImageSelect';
import EmailDesignTemplateCardSelect from '../../Components/Select/EmailDesignTemplateCardSelect';
import { BlobDto, WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto } from '../../Repository/eventbookyapi';
import { DocumentService } from '../../Services/ImageService';
import ColorPicker from '../../Components/Color/ColorPicker';
import { BookySelectDropDown } from '../../Components/Forms/BookySelectDropDown';
import { EmailTemplatePurpose } from '../../Types/EmailTemplatePurpose';
import { stringOrDate } from 'react-big-calendar';
import { AvailableEmailPurpose } from '../../Mappers/mapAvailablePurpose';
import PageHelp from '../../Components/Page/PageHelp';
import { HelpModal } from '../../Components/Page/HelpModal';

const DEFAULT_ACCENT_COLOR = "#4a6cf7";
const DEFAULT_TEXT_COLOR = "#ffffff";

export const CreateEmailTemplateButton = (props: any) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState(false);
    const [html, setHtml] = useState("" as string);
    const [images, setImages] = useState([] as BlobDto[]);
    const [isImageModalOpen, setIsImageModalOpen] = useState(false as boolean);
    const [selectedLogo, setSelectedLogo] = useState("" as string);
    const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR as string);
    const [textColor, setTextColor] = useState(DEFAULT_TEXT_COLOR as string);
    const [purpose, setPurpose] = useState(undefined as EmailTemplatePurpose | undefined);
    const [editorKey, setEditorKey] = useState(0);

    const GetText = TextService.GetText;

    // Opens the modal empty, each time openBlankRequestId changes.
    useEffect(() => {
        if (!props.openBlankRequestId) {
            return;
        }

        clearForm();
        setPurpose(undefined);
        setOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.openBlankRequestId]);

    // Opens the modal prefilled with a OneFront example template, each time prefillRequestId changes.
    useEffect(() => {
        const template = props.prefillTemplate as WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto | undefined;
        if (!props.prefillRequestId || !template) {
            return;
        }

        clearForm();

        const design = template.design;
        const designTemplate = design?.emailDesignTemplate as EmailTemplateType | undefined;
        // Unspecified is 0, so a falsy value means no design.
        const hasDesign = !!designTemplate;
        const body = template.body ?? "";
        const templatePurpose = props.isAutomation ? template.purpose as EmailTemplatePurpose | undefined : undefined;

        setPurpose(templatePurpose);
        form.setFieldsValue({
            purpose: templatePurpose,
            templateName: template.name,
            subject: template.subject,
            body: body,
            enableEmailDesign: hasDesign,
            emailDesignTemplate: hasDesign ? designTemplate : undefined,
            header: hasDesign ? design?.header ?? undefined : undefined,
            imageUrl: hasDesign ? design?.imageUrl ?? undefined : undefined,
            accentColor: hasDesign ? design?.accentColor ?? undefined : undefined,
            textColor: hasDesign ? design?.textColor ?? undefined : undefined,
        });

        setHtml(body);
        setSelectedLogo(design?.imageUrl ?? "");
        setAccentColor(design?.accentColor || DEFAULT_ACCENT_COLOR);
        setTextColor(design?.textColor || DEFAULT_TEXT_COLOR);
        setOpen(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.prefillRequestId]);
    const hasEmailDesign = Form.useWatch("enableEmailDesign", form) === true;
    const selectedEmailDesignTemplate = Form.useWatch("emailDesignTemplate", form) as EmailTemplateType | undefined;

    const onCancel = () => {
        clearForm();
        setOpen(false);
    };

    const onHtmlChange = (value: string) => {
        setHtml(value);
        form.setFieldValue("body", value);
    }

    const loadImages = async () => {
        const publicImages = await DocumentService.GetPublicImages();
        setImages(publicImages);
    }

    const openImagePicker = async () => {
        await loadImages();
        setIsImageModalOpen(true);
    }

    const onImageSelect = (image: string) => {
        setSelectedLogo(image);
        form.setFieldValue("imageUrl", image);
    }

    const onEmailDesignToggle = (event: any) => {
        if (event?.target?.checked !== true) {
            form.setFieldValue("emailDesignTemplate", undefined);
            form.setFieldValue("header", undefined);
            form.setFieldValue("imageUrl", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            form.setFieldValue("notificationEmail", undefined);
            setSelectedLogo("");
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
            return;
        }

        form.setFieldValue("emailDesignTemplate", EmailTemplateType.Classic);
    }

    const onEmailDesignTemplateChange = (templateType: EmailTemplateType) => {
        if (templateType !== EmailTemplateType.Classic) {
            form.setFieldValue("imageUrl", undefined);
            setSelectedLogo("");
        }

        if (templateType !== EmailTemplateType.Header) {
            form.setFieldValue("header", undefined);
            form.setFieldValue("accentColor", undefined);
            form.setFieldValue("textColor", undefined);
            setAccentColor(DEFAULT_ACCENT_COLOR);
            setTextColor(DEFAULT_TEXT_COLOR);
        }

        if (templateType === EmailTemplateType.Header) {
            const initialAccentColor = form.getFieldValue("accentColor") || DEFAULT_ACCENT_COLOR;
            const initialTextColor = form.getFieldValue("textColor") || DEFAULT_TEXT_COLOR;
            form.setFieldValue("accentColor", initialAccentColor);
            form.setFieldValue("textColor", initialTextColor);
            setAccentColor(initialAccentColor);
            setTextColor(initialTextColor);
        }
    }

    const onAccentColorChanged = (hex: string) => {
        setAccentColor(hex);
        form.setFieldValue("accentColor", hex);
    }

    const onTextColorChanged = (hex: string) => {
        setTextColor(hex);
        form.setFieldValue("textColor", hex);
    }

    const onPurposeChange = (purpose: any) => {
        setPurpose(purpose);
        form.setFieldValue("purpose", purpose);
    }

    const onCreate = async (values: any) => {
        const design = getDesignFromFormValues(values);

        try {
            await EmailTemplateService.CreateTemplate({
                templateName: values.templateName,
                subject: values.subject,
                design: design,
                notificationEmail: values.notificationEmail,
                body: values.body,
                purpose: values.purpose,
            });
        } catch (error) {
            message.warning(GetText("CreateTemplate_ErrorAlreadyExist"));
            return;
        }

        setOpen(false);
        clearForm();
        props.reloadTemplate();
    }

    const clearForm = () => {
        form.resetFields();
        form.setFieldValue("enableEmailDesign", false);
        form.setFieldValue("sendWhenUserIsCreated", false);
        setHtml("");
        setSelectedLogo("");
        setAccentColor(DEFAULT_ACCENT_COLOR);
        setTextColor(DEFAULT_TEXT_COLOR);
        // The editor only reads initialHtml on mount, so remount it to show the new content.
        setEditorKey((previous) => previous + 1);
    }

    return (
        <>
            <Modal
                width={900}
                open={open}
                title={props.isAutomation ? GetText("AutomaticSending_CreateModalTitle") : GetText("CreateTemplate_ModalHeader")}
                okText={props.isAutomation ? GetText("AutomaticSending_CreateModalTitle") : GetText("CreateTemplate_CreateText")}
                cancelText={GetText("CreateTemplate_CancelText")}
                footer={(_, { OkBtn, CancelBtn }) =>
                    <div className="email-template-modal-footer">
                        <EmailTemplatePreviewButton form={form} />
                        <Space><CancelBtn /><OkBtn /></Space>
                    </div>}
                onOk={() => {
                    form
                        .validateFields()
                        .then((values) => {
                            onCreate(values);
                        })
                        .catch((info) => {
                            console.log('Validate Failed:', info);
                        });
                }}
                onCancel={onCancel}
            >
                <Form form={form} onFinish={onCreate} layout='vertical'>
                    <Col span={24}>
                        {props.isAutomation && <BookySelectDropDown
                            name="purpose"
                            label={GetText("AutomaticSending_EventLabel")}
                            handleChange={onPurposeChange}
                            placeholder={GetText("AutomaticSending_EventPlaceholder")}
                            options={AvailableEmailPurpose}
                            required
                            requiredMessage={GetText("CreateTemplate_RequiredText")}
                        />}

                        <BookyInput name="templateName" requiredMessage={GetText("CreateTemplate_RequiredText")} label={props.isAutomation ? GetText("AutomaticSending_NameLabel") : GetText("CreateTemplate_PlaceHolderText")} />
                        <BookyInput name="subject" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Subject")} />
                        <OneFrontHtmlEditor key={editorKey} initialHtml={html} enablePaymentLink={props.enablePaymentLink} onHtmlChange={onHtmlChange} label={GetText("CreateTemplate_Body")} name="body" />

                        <BookyCheckbox alignLabelRight label={GetText("CreateTemplate_AddDesign")} name="enableEmailDesign" onChange={onEmailDesignToggle}></BookyCheckbox>

                        {hasEmailDesign ? <>
                            <Form.Item
                                label={GetText("CreateTemplate_DesignTemplate")}
                                name="emailDesignTemplate"
                                rules={[{ required: true, message: GetText("CreateTemplate_RequiredText") }]}
                            >
                                <EmailDesignTemplateCardSelect onTemplateSelected={onEmailDesignTemplateChange} />
                            </Form.Item>

                            {selectedEmailDesignTemplate === EmailTemplateType.Classic ? <>
                                <Form.Item
                                    name="imageUrl"
                                    hidden
                                    dependencies={["enableEmailDesign", "emailDesignTemplate"]}
                                    rules={[
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (getFieldValue("enableEmailDesign") && getFieldValue("emailDesignTemplate") === EmailTemplateType.Classic && !value) {
                                                    return Promise.reject(new Error(GetText("CreateTemplate_LogoRequired")));
                                                }

                                                return Promise.resolve();
                                            },
                                        }),
                                    ]}
                                >
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_Logo")}>
                                    <Button onClick={openImagePicker}>{GetText("CreateTemplate_SelectLogo")}</Button>
                                </Form.Item>

                                {selectedLogo ? <Image width={220} height={140} preview={true} src={selectedLogo}></Image> : ""}
                            </> : ""}

                            {selectedEmailDesignTemplate === EmailTemplateType.Header ? <>
                                <BookyInput name="header" requiredMessage={GetText("CreateTemplate_RequiredText")} label={GetText("CreateTemplate_Header")} />

                                <Form.Item label={GetText("CreateTemplate_AccentColor")}>
                                    <ColorPicker color={accentColor} onChangeComplete={onAccentColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="accentColor">
                                    <Input />
                                </Form.Item>

                                <Form.Item label={GetText("CreateTemplate_TextColor")}>
                                    <ColorPicker color={textColor} onChangeComplete={onTextColorChanged} />
                                </Form.Item>
                                <Form.Item hidden name="textColor">
                                    <Input />
                                </Form.Item>
                            </> : ""}
                        </> : ""}

                        {!props.hidePurpose && !props.isAutomation && <BookySelectDropDown helpTitle={GetText("CreateTemplate_TemplatePurposeTitleHelp")} help={GetText("CreateTemplate_TemplatePurposeHelp")} name="purpose" label={GetText("CreateTemplate_TemplatePurpose")} handleChange={onPurposeChange} placeholder={GetText("CreateTemplate_SelectPurposeEmail")} options={AvailableEmailPurpose} allowClear />}


                        <PageHelp>
                        </PageHelp>
                        {purpose === EmailTemplatePurpose.MemberGetsActivatedNotification && (
                            <BookyInput name="notificationEmail" validateEmailIfFilledIn requiredMessage={GetText("CreateTemplate_RequiredEmail")} label={GetText("CreateTemplate_NotificationEmail")} placeholder={GetText("CreateTemplate_TypeNotificationEmail")} />
                        )}
                    </Col>
                </Form>
            </Modal >

            <Modal
                title={GetText("MyEventDetails_SelectImage")}
                width={850}
                open={isImageModalOpen}
                onOk={() => { setIsImageModalOpen(false) }}
                onCancel={() => { setIsImageModalOpen(false) }}
            >
                <ImageSelect images={images} isPublicImage={true} loadImages={loadImages} onImageSelect={onImageSelect} selectedImage={selectedLogo} />
            </Modal>

            {!props.hideButton && <div>
                <BookyButton
                    type="primary"
                    fullWidth={false}
                    text={props.isAutomation ? GetText("AutomaticSending_CreateModalTitle") : GetText("CreateTemplate_CreateText")}
                    onClick={() => {
                        clearForm();
                        setOpen(true);
                    }}
                >

                </BookyButton>
            </div>}
        </>
    );
};

export default CreateEmailTemplateButton;